import React, { useEffect, useState, useCallback } from "react";
import {
  getCrewUserByUserId,
  getReviewTimeSheetsList,
  updateMyTimeCardStatus,
  getDaySummary,
} from "../API";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "./Loader";
import { isChecklistApproverFlag, getReviewableCrewIds } from "../utils/auth";

const ReviewTimeSheet = () => {
  const [inReviewTeamHours, setInReviewTeamHours] = useState([]);
  const [inReviewEquipmentHours, setInReviewEquipmentHours] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [daySummary, setDaySummary] = useState([]);
  const [isLoadingColor, setIsLoadingColor] = useState(false);

  const navigate = useNavigate();
  const user_id = localStorage.getItem("user_id");
  const role = localStorage.getItem("role_name");
  // Checklist approvers get read access to ALL timesheets on top of their
  // crew role (additive capability, same flag-driven gating as UnitJob and
  // MyProjectCheckList). Write actions stay confined to the crews the user's
  // native role reviews: reviewableCrewIds is null when not scope-limited,
  // otherwise the set of crew ids the user may still act on.
  const isChecklistApprover = isChecklistApproverFlag();
  const [reviewableCrewIds, setReviewableCrewIds] = useState(null);

  const canReviewRow = (item) =>
    !reviewableCrewIds || reviewableCrewIds.has(Number(item?.crew_id));

  const onClickReview = async (item) => {
    try {
      // Rows outside the user's native review scope are view-only: opening
      // them must not move a submitted sheet into in-review
      if (item?.time_sheet_status_id === 2 && canReviewRow(item)) {
        const res = await updateMyTimeCardStatus(
          { time_sheet_status_id: 3 },
          item?.time_sheet_id
        );
        if (res?.status === 200) toast.success("This time sheet is in-review state");
      }
      navigate(`/review-timecard/${item?.time_sheet_id}/${item?.crew_id}`);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const fetchDaySummarySafe = async (rows) => {
    // Important: prevent undefined ids
    const validRows = (rows || []).filter((r) => r?.time_sheet_id);

    // Optional: match MyTimesheet condition
    const filteredForSummary = validRows.filter((r) => r?.time_sheet_status_id < 4);
    if (!filteredForSummary.length) {
      setDaySummary([]);
      return;
    }

    try {
      const summaryRes = await getDaySummary(filteredForSummary);
      setDaySummary(summaryRes?.data || []);
    } catch (e) {
      // Don't break page if summary fails
      setDaySummary([]);
    }
  };

  const getAllInReviewTimeSheets = useCallback(async () => {
    setIsLoading(true);
    setIsLoadingColor(true);

    setInReviewTeamHours([]);
    setInReviewEquipmentHours([]);
    setDaySummary([]);

    try {
      // ADMIN / CHECKLIST APPROVER FLOW — unfiltered, sees every crew's sheets.
      // Checked before the user flow so crew-role approvers aren't scoped down.
      if (isChecklistApprover || (!user_id && role === "Admin")) {
        setReviewableCrewIds(await getReviewableCrewIds(role, user_id));

        const query = `time_sheet_status_ids=0&time_sheet_status_ids=2&time_sheet_status_ids=3`;
        const res = await getReviewTimeSheetsList(query);

        const allRows = res?.status === 200 ? res?.data || [] : [];

        const teamRows = allRows.filter((r) => r?.time_entry_type_id === 1);
        const equipmentRows = allRows.filter((r) => r?.time_entry_type_id === 2);

        setInReviewTeamHours(teamRows);
        setInReviewEquipmentHours(equipmentRows);

        await fetchDaySummarySafe(allRows);

        setIsLoading(false);
        setIsLoadingColor(false);
        return;
      }

      // USER FLOW
      if (user_id) {
        const crewRes = await getCrewUserByUserId(user_id);

        let filterCrew = [];
        if (role === "General Foreman" || role === "Foreman GeneralForeman") {
          filterCrew = crewRes?.data?.filter((d) => d?.crew_role_id === 2) || [];
        } else if (role === "Ops Manager" || role === "Foreman OpsManager") {
          filterCrew = crewRes?.data?.filter((d) => d?.crew_role_id === 3) || [];
        } else if (role === "GeneralForeman Ops Manager") {
          filterCrew =
            crewRes?.data?.filter((d) => [2, 3].includes(d?.crew_role_id)) || [];
        } else {
          filterCrew = crewRes?.data || [];
        }

        // Fetch all crews first
        const results = await Promise.all(
          filterCrew.map(async (ele) => {
            const query =
              `time_sheet_status_ids=0&time_sheet_status_ids=2&time_sheet_status_ids=3&crew_id=${ele?.crew_id}`;
            const res = await getReviewTimeSheetsList(query);
            return res?.status === 200 ? res?.data || [] : [];
          })
        );

        const allRows = results.flat().filter(Boolean);

        const teamRows = allRows.filter((r) => r?.time_entry_type_id === 1);
        const equipmentRows = allRows.filter((r) => r?.time_entry_type_id === 2);

        setInReviewTeamHours(teamRows);
        setInReviewEquipmentHours(equipmentRows);

        await fetchDaySummarySafe(allRows);

        setIsLoading(false);
        setIsLoadingColor(false);
        return;
      }

      // default fallback
      setIsLoading(false);
      setIsLoadingColor(false);
    } catch (e) {
      setIsLoading(false);
      setIsLoadingColor(false);
      toast.error("Failed to load timesheets");
    }
  }, [user_id, role, isChecklistApprover]);

  const displayWeeklyData = (timeSheetId) => {
    if (!daySummary?.length) return null;

    const result = daySummary.map((item) => {
      if (item?.data?.length > 0) {
        const data = item.data
          .map((dataItem) => ({
            day: dataItem?.day?.charAt(0),
            color: dataItem?.color,
            numeric: dataItem?.dayNumeric,
            fontsize: dataItem?.weight,
          }))
          .sort((a, b) => a.numeric - b.numeric);

        return { id: item?.time_sheet_id, data };
      }

      return {
        day: item?.day?.charAt(0),
        color: item?.color,
        numeric: item?.dayNumeric,
        fontsize: item?.weight,
      };
    });

    // grouped (preferred)
    if (result?.[0]?.id) {
      const sheet = result.find((x) => parseInt(x?.id) === parseInt(timeSheetId));
      if (!sheet?.data?.length) return null;

      return sheet.data.map((d, idx) => (
        <div
          // key={day?.day}
          // style={{ color: day?.color }}
          className="day"
        >
          <div className={`textDay weight-${d?.fontsize}`}>
              {d?.day}
            </div>
            <div className={`statusDay ${d?.color}`}></div>
        </div>
      ));
    }

    // fallback (non-grouped)
    return result.map((d, idx) => (
      <div key={idx} style={{ color: d.color }} className={`day ${d.fontsize}`}>
        {d.day}
      </div>
    ));
  };

  useEffect(() => {
    getAllInReviewTimeSheets();
  }, [getAllInReviewTimeSheets]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between" }} className="timesheet">
        <h2 className="title">Review Timesheets</h2>
      </div>

      {!isLoading ? (
        <table className="table table-striped mb-5 tableV2">
          <thead>
            <tr>
              <th scope="col" className="table-heading"></th>
              <th scope="col" className="table-heading">Crew Id</th>
              <th scope="col" className="table-heading">Foreman</th>
              <th scope="col" className="table-heading">Week</th>
              <th scope="col" className="table-heading labor-hour">Labor Hrs</th>
              <th scope="col" className="table-heading equipment-hour">EQP Hrs</th>
              <th scope="col" className="table-heading" style={{"textAlign": "center"}}>Day Summary</th>
              <th scope="col" className="table-heading"></th>
            </tr>
          </thead>

          <tbody>
            {inReviewTeamHours?.length > 0 ? (
              inReviewTeamHours.map((teamItem, key) => {
                console.log('-------------',teamItem);
                const equipmentTime = inReviewEquipmentHours.find(
                  (equipmentItem) => teamItem?.time_sheet_id === equipmentItem?.time_sheet_id
                );

                return (
                  <tr
                    key={key}
                    className={[0, 1].includes(teamItem?.time_sheet_status_id) ? "open-sheet" : "review-sheet"}
                  >
                    <td><div className={[0, 1].includes(teamItem?.time_sheet_status_id) ? "open-sheet" : "review-sheet"}></div></td>
                    <td className="crew-id"><div>{teamItem?.virtual_crew_id || "NA"}</div></td>
                    <td className="fm-name">{teamItem?.foreman_name}</td>
                    <td className="week" >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-calendar w-4 h-4 text-slate-400" aria-hidden="true"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg>
                      <div>{teamItem?.week}</div>
                    </td>
                    <td className="labor-hour" style={{    "width": "10%"}}>{teamItem?.hours || 0}</td>
                    <td className="equipment-hour">{equipmentTime?.hours || 0}</td>

                    <td className="DaySummeryReviewTimesheet" style={{"textAlign": "center"}}>
                      {isLoadingColor ? (
                        <div className="days">
                          <div className="day">M</div>
                          <div className="day">T</div>
                          <div className="day">W</div>
                          <div className="day">T</div>
                          <div className="day">F</div>
                          <div className="day">S</div>
                          <div className="day">S</div>
                        </div>
                      ) : (
                        <div className="days">{displayWeeklyData(teamItem?.time_sheet_id)}</div>
                      )}
                    </td>

                    <td style={{    "width": "12%"}}>
                      <button
                        type="button"
                        className="primary-btn submit-btn"
                        style={{ marginRight: "20px"}}
                        onClick={() => onClickReview(teamItem)}
                      >
                        {[0, 1].includes(teamItem?.time_sheet_status_id) || !canReviewRow(teamItem)
                          ? "View"
                          : "Review"}
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={7}>No data found</td>
              </tr>
            )}
          </tbody>
        </table>
      ) : (
        <Loader />
      )}
    </div>
  );
};

export default ReviewTimeSheet;
